<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { resetPassword } from '../db/auth.js';

const router = useRouter();
const route = useRoute();

const email = ref('');
const code = ref(['', '', '', '', '', '']);
const newPassword = ref('');
const confirmPassword = ref('');
const loading = ref(false);
const error = ref('');
const success = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Refs para los inputs de código
const inputRefs = ref([]);

// Modal state
const showModal = ref(false);
const modalType = ref('error');
const modalTitle = ref('');
const modalMessage = ref('');

const codeString = computed(() => code.value.join(''));

const isCodeComplete = computed(() => {
    return code.value.every(digit => digit !== '');
});

const passwordStrength = computed(() => {
    const p = newPassword.value;
    if (!p) return { level: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 10) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[a-z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;

    if (score <= 2) return { level: score, label: 'Débil', color: '#ff6b6b' };
    if (score === 3) return { level: score, label: 'Media', color: '#ffa94d' };
    if (score === 4) return { level: score, label: 'Buena', color: '#51cf66' };
    return { level: score, label: 'Excelente', color: '#00C2A8' };
});

function showModalDialog(type, title, message) {
    modalType.value = type;
    modalTitle.value = title;
    modalMessage.value = message;
    showModal.value = true;
}

function closeModal() {
    showModal.value = false;
    if (success.value) {
        router.push('/login');
    }
}

onMounted(() => {
    email.value = String(route.query.email || '');

    nextTick(() => {
        if (inputRefs.value[0]) {
            inputRefs.value[0].focus();
        }
    });
});

function handleInput(index, event) {
    const value = event.target.value;

    if (!/^\d*$/.test(value)) {
        event.target.value = code.value[index];
        return;
    }

    if (value.length > 1) {
        const digits = value.slice(0, 6).split('');
        digits.forEach((digit, i) => {
            if (i < 6) code.value[i] = digit;
        });
        const lastIndex = Math.min(digits.length - 1, 5);
        if (inputRefs.value[lastIndex]) {
            inputRefs.value[lastIndex].focus();
        }
        return;
    }

    code.value[index] = value;

    if (value && index < 5) {
        if (inputRefs.value[index + 1]) {
            inputRefs.value[index + 1].focus();
        }
    }
}

function handleKeyDown(index, event) {
    if (event.key === 'Backspace' && !code.value[index] && index > 0) {
        code.value[index - 1] = '';
        if (inputRefs.value[index - 1]) {
            inputRefs.value[index - 1].focus();
        }
    }

    if (event.key === 'ArrowLeft' && index > 0) {
        inputRefs.value[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < 5) {
        inputRefs.value[index + 1]?.focus();
    }
}

async function submit() {
    if (!isCodeComplete.value) {
        error.value = 'Por favor completa el código de 6 dígitos';
        return;
    }

    if (!newPassword.value) {
        error.value = 'Por favor ingresa una nueva contraseña';
        return;
    }

    if (newPassword.value !== confirmPassword.value) {
        error.value = 'Las contraseñas no coinciden';
        return;
    }

    if (passwordStrength.value.level < 3) {
        error.value = 'La contraseña es demasiado débil';
        return;
    }

    error.value = '';
    loading.value = true;

    try {
        const res = await resetPassword({
            username: email.value,
            code: codeString.value,
            new_password: newPassword.value
        });

        loading.value = false;

        if (!res.ok) {
            error.value = res.message || 'Código inválido o expirado';
            code.value = ['', '', '', '', '', ''];
            if (inputRefs.value[0]) {
                inputRefs.value[0].focus();
            }
            return;
        }

        success.value = true;
        showModalDialog('success', '¡Contraseña cambiada!', 'Tu contraseña ha sido actualizada exitosamente. Ahora puedes iniciar sesión.');
    } catch (e) {
        loading.value = false;
        error.value = e?.message || 'Error al cambiar la contraseña';
        console.error('Error en reset:', e);
    }
}
</script>

<template>
    <main class="reset-container">
        <!-- Custom Modal -->
        <Transition name="modal">
            <div v-if="showModal" class="modal-overlay" @click="closeModal">
                <div class="modal-container" @click.stop>
                    <div class="modal-icon" :class="modalType">
                        <span class="material-symbols-rounded">
                            {{ modalType === 'error' ? 'cancel' : 'check_circle' }}
                        </span>
                    </div>
                    <h3 class="modal-title">{{ modalTitle }}</h3>
                    <p class="modal-message">{{ modalMessage }}</p>
                    <button class="modal-btn" @click="closeModal">OK</button>
                </div>
            </div>
        </Transition>

        <div class="reset-wrapper">
            <!-- Left Side - Form -->
            <div class="form-side">
                <div class="form-content">
                    <div class="header">
                        <h1 class="welcome-text">Nueva contraseña</h1>
                        <h2 class="brand-name">{{ email }}</h2>
                        <p class="subtitle">Ingresa el código de recuperación y tu nueva contraseña</p>
                    </div>

                    <form @submit.prevent="submit" class="reset-form">
                        <div v-if="error" class="error-message">
                            {{ error }}
                        </div>

                        <!-- Code Inputs -->
                        <div class="input-group">
                            <label class="input-label">Código de recuperación</label>
                            <div class="code-inputs">
                                <input v-for="(digit, index) in code" :key="index" :ref="el => inputRefs[index] = el"
                                    v-model="code[index]" type="text" inputmode="numeric" maxlength="1"
                                    class="code-digit" :class="{ 'filled': digit, 'error': error }"
                                    @input="handleInput(index, $event)" @keydown="handleKeyDown(index, $event)"
                                    :disabled="loading || success" />
                            </div>
                        </div>

                        <!-- Nueva Contraseña -->
                        <div class="input-group">
                            <label class="input-label">Nueva contraseña</label>
                            <div class="password-wrapper">
                                <input v-model="newPassword" :type="showPassword ? 'text' : 'password'"
                                    placeholder="••••••••••" class="input-field" />
                                <button type="button" class="toggle-password" @click="showPassword = !showPassword">
                                    <span class="material-symbols-rounded">
                                        {{ showPassword ? 'visibility_off' : 'visibility' }}
                                    </span>
                                </button>
                            </div>

                            <!-- Password Strength -->
                            <div v-if="newPassword" class="password-strength">
                                <div class="strength-bar">
                                    <div class="strength-fill"
                                        :style="{ width: `${(passwordStrength.level / 5) * 100}%`, backgroundColor: passwordStrength.color }">
                                    </div>
                                </div>
                                <span class="strength-label" :style="{ color: passwordStrength.color }">
                                    {{ passwordStrength.label }}
                                </span>
                            </div>
                        </div>

                        <!-- Confirmar Contraseña -->
                        <div class="input-group">
                            <label class="input-label">Confirmar contraseña</label>
                            <div class="password-wrapper">
                                <input v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                                    placeholder="••••••••••" class="input-field" />
                                <button type="button" class="toggle-password"
                                    @click="showConfirmPassword = !showConfirmPassword">
                                    <span class="material-symbols-rounded">
                                        {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <!-- Submit Button -->
                        <button type="submit" class="submit-btn" :disabled="loading || success">
                            <span v-if="!loading">CAMBIAR CONTRASEÑA</span>
                            <span v-else class="loading-spinner"></span>
                        </button>

                        <p class="footer-text">
                            ¿Recordaste tu contraseña?
                            <router-link to="/login" class="link-btn">Iniciar sesión</router-link>
                        </p>
                    </form>
                </div>
            </div>

            <!-- Right Side - Illustration -->
            <div class="illustration-side">
                <div class="illustration-content">
                    <div class="moon"></div>
                    <div class="stars">
                        <div class="star" style="top: 15%; left: 20%;"></div>
                        <div class="star" style="top: 25%; left: 60%;"></div>
                        <div class="star" style="top: 40%; left: 30%;"></div>
                        <div class="star" style="top: 50%; left: 70%;"></div>
                        <div class="star" style="top: 65%; left: 15%;"></div>
                    </div>
                    <div class="mountains">
                        <div class="mountain mountain-1"></div>
                        <div class="mountain mountain-2"></div>
                        <div class="mountain mountain-3"></div>
                    </div>
                    <div class="buildings">
                        <div class="building building-1">
                            <div class="building-door"></div>
                            <div class="building-windows">
                                <div class="window"></div>
                                <div class="window"></div>
                                <div class="window"></div>
                            </div>
                        </div>
                        <div class="building building-2">
                            <div class="building-roof"></div>
                            <div class="building-windows">
                                <div class="window"></div>
                                <div class="window"></div>
                            </div>
                        </div>
                    </div>
                    <div class="plants">
                        <div class="plant plant-1"></div>
                        <div class="plant plant-2"></div>
                    </div>
                    <div class="water"></div>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Cinzel:wght@600;700;800&display=swap');

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.reset-container {
    display: flex;
    min-height: 100vh;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
    padding: 2rem;
}

.reset-wrapper {
    max-width: 900px;
    width: 100%;
    display: flex;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
    border-radius: 16px;
    overflow: hidden;
    background: rgba(20, 24, 30, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(198, 146, 61, 0.2);
}

/* ===== LEFT SIDE - FORM ===== */
.form-side {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: transparent;
}

.form-content {
    width: 100%;
    max-width: 380px;
}

.header {
    margin-bottom: 1.5rem;
    text-align: center;
}

.welcome-text {
    font-size: 1rem;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.15rem;
}

.brand-name {
    font-size: 1.1rem;
    font-weight: 900;
    font-family: 'Playfair Display', 'Cinzel', 'Times New Roman', serif;
    text-transform: uppercase;
    background: linear-gradient(135deg, 
        #FFD700 0%, 
        #FFE55C 10%,
        #FFD700 20%,
        #F4D03F 35%,
        #FFD700 45%,
        #C6923D 60%,
        #9D6B27 75%,
        #C6923D 85%,
        #FFD700 95%,
        #FFE55C 100%);
    background-size: 300% 300%;
    animation: shimmer 2.5s ease-in-out infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.15em;
    margin: 0 0 0.5rem 0;
    word-break: break-all;
    text-shadow: 
        2px 3px 0px rgba(0, 0, 0, 1),
        4px 5px 8px rgba(0, 0, 0, 0.9),
        6px 8px 15px rgba(0, 0, 0, 0.8),
        0px 1px 3px rgba(255, 215, 0, 0.6),
        0px -1px 2px rgba(114, 68, 16, 0.8),
        inset 0px 1px 0px rgba(255, 255, 255, 0.3);
    filter: 
        drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.9))
        drop-shadow(1px 2px 3px rgba(255, 215, 0, 0.4));
    position: relative;
    transform: perspective(100px) rotateX(2deg);
}

@keyframes shimmer {
    0%, 100% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
}

.subtitle {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;
}

.reset-form {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.error-message {
    padding: 0.5rem;
    background: #ffe5e5;
    border-left: 3px solid #ff6b6b;
    color: #d63031;
    font-size: 0.75rem;
    border-radius: 4px;
}

/* Input Groups */
.input-group {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.input-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
}

.input-field {
    width: 100%;
    padding: 0.5rem 0;
    border: none;
    border-bottom: 1.5px solid rgba(255, 255, 255, 0.3);
    background: transparent;
    font-size: 0.85rem;
    font-family: 'Poppins', sans-serif;
    color: #ffffff;
    transition: border-color 0.3s;
    outline: none;
}

.input-field::placeholder {
    color: rgba(255, 255, 255, 0.5);
}

.input-field:focus {
    border-bottom-color: #C6923D;
}

/* Password Wrapper */
.password-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.toggle-password {
    position: absolute;
    right: 0;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    transition: color 0.3s;
}

.toggle-password:hover {
    color: #C6923D;
}

.toggle-password .material-symbols-rounded {
    font-size: 1.25rem;
}

/* Password Strength */
.password-strength {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
}

.strength-bar {
    flex: 1;
    height: 3px;
    background: #e0e0e0;
    border-radius: 2px;
    overflow: hidden;
}

.strength-fill {
    height: 100%;
    transition: all 0.3s;
}

.strength-label {
    font-size: 0.7rem;
    font-weight: 600;
}

/* Code Inputs */
.code-inputs {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
}

.code-digit {
    width: 2.5rem;
    height: 3rem;
    font-size: 1.25rem;
    font-weight: 700;
    text-align: center;
    border: none;
    border-bottom: 2px solid #e0e0e0;
    background: transparent;
    color: #333;
    transition: all 0.3s;
    outline: none;
    font-family: 'Poppins', sans-serif;
}

.code-digit:focus {
    border-bottom-color: #C6923D;
    transform: scale(1.05);
}

.code-digit.filled {
    border-bottom-color: #DFC05E;
    color: #DFC05E;
}

.code-digit.error {
    border-bottom-color: #ff6b6b;
    animation: shake 0.5s;
}

@keyframes shake {

    0%,
    100% {
        transform: translateX(0);
    }

    25% {
        transform: translateX(-5px);
    }

    75% {
        transform: translateX(5px);
    }
}

.code-digit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Submit Button */
.submit-btn {
    width: 100%;
    padding: 0.65rem;
    background: linear-gradient(135deg, #C6923D, #DFC05E);
    color: #000000;
    border: none;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.3s;
    margin-top: 0.35rem;
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(198, 146, 61, 0.4);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.loading-spinner {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

/* Footer */
.footer-text {
    text-align: center;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 0.35rem;
}

.link-btn {
    color: #C6923D;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s;
}

.link-btn:hover {
    color: #DFC05E;
    text-decoration: underline;
}

/* ===== RIGHT SIDE - ILLUSTRATION ===== */
.illustration-side {
    flex: 1;
    position: relative;
    background: linear-gradient(135deg, #C6923D 0%, #DFC05E 50%, #F7ED7D 100%);
    overflow: hidden;
}

.illustration-content {
    position: relative;
    width: 100%;
    height: 100%;
}

.moon {
    position: absolute;
    top: 10%;
    right: 10%;
    width: 80px;
    height: 80px;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 0 40px rgba(255, 255, 255, 0.5);
    z-index: 2;
}

.stars {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1;
}

.star {
    position: absolute;
    width: 4px;
    height: 4px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
    animation: twinkle 2s ease-in-out infinite;
}

@keyframes twinkle {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.3;
    }
}

.star:nth-child(2) {
    animation-delay: 0.5s;
}

.star:nth-child(3) {
    animation-delay: 1s;
}

.star:nth-child(4) {
    animation-delay: 1.5s;
}

.star:nth-child(5) {
    animation-delay: 2s;
}

.mountains {
    position: absolute;
    bottom: 35%;
    width: 100%;
    height: 40%;
    z-index: 3;
}

.mountain {
    position: absolute;
    bottom: 0;
}

.mountain-1 {
    left: 10%;
    width: 0;
    height: 0;
    border-left: 150px solid transparent;
    border-right: 150px solid transparent;
    border-bottom: 250px solid rgba(106, 90, 205, 0.6);
}

.mountain-2 {
    left: 35%;
    width: 0;
    height: 0;
    border-left: 120px solid transparent;
    border-right: 120px solid transparent;
    border-bottom: 200px solid rgba(138, 43, 226, 0.5);
}

.mountain-3 {
    right: 15%;
    width: 0;
    height: 0;
    border-left: 100px solid transparent;
    border-right: 100px solid transparent;
    border-bottom: 180px solid rgba(147, 112, 219, 0.4);
}

.buildings {
    position: absolute;
    bottom: 20%;
    width: 100%;
    height: 40%;
    z-index: 4;
}

.building {
    position: absolute;
    background: linear-gradient(135deg, #ff9a9e, #fad0c4);
    border-radius: 8px 8px 0 0;
}

.building-1 {
    left: 25%;
    width: 120px;
    height: 200px;
    background: linear-gradient(135deg, #ffa07a, #ff8c69);
}

.building-door {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 80px;
    background: linear-gradient(135deg, #ff6b6b, #ff5252);
    border-radius: 4px 4px 0 0;
}

.building-windows {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 20px;
}

.window {
    width: 100%;
    height: 20px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
}

.building-2 {
    right: 20%;
    width: 140px;
    height: 160px;
    background: linear-gradient(135deg, #fbc2eb, #a6c1ee);
}

.building-roof {
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 70px solid transparent;
    border-right: 70px solid transparent;
    border-bottom: 30px solid #f093fb;
}

.plants {
    position: absolute;
    bottom: 15%;
    width: 100%;
    z-index: 5;
}

.plant {
    position: absolute;
    width: 60px;
    height: 80px;
    background: linear-gradient(135deg, #d4a5a5, #c48b9f);
    border-radius: 50% 50% 0 0;
}

.plant-1 {
    left: 15%;
    transform: rotate(-10deg);
}

.plant-2 {
    right: 30%;
    transform: rotate(10deg);
}

.water {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 20%;
    background: linear-gradient(180deg,
            rgba(147, 197, 253, 0.4) 0%,
            rgba(96, 165, 250, 0.6) 50%,
            rgba(59, 130, 246, 0.8) 100%);
    z-index: 6;
}

/* Responsive */
@media (max-width: 968px) {
    .reset-wrapper {
        flex-direction: column;
    }

    .illustration-side {
        min-height: 300px;
        order: -1;
    }

    .form-side {
        padding: 2rem 1rem;
    }
}

@media (max-width: 480px) {
    .reset-container {
        padding: 1rem;
    }

    .reset-wrapper {
        border-radius: 12px;
    }

    .code-digit {
        width: 2rem;
        height: 2.5rem;
        font-size: 1rem;
    }

    .form-content {
        max-width: 100%;
    }
}

/* ===== CUSTOM MODAL ===== */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    max-width: 400px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.modal-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: iconPop 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes iconPop {
    0% {
        transform: scale(0);
        opacity: 0;
    }

    50% {
        transform: scale(1.1);
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

.modal-icon.error {
    background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
}

.modal-icon.success {
    background: linear-gradient(135deg, #51cf66, #00C2A8);
}

.modal-icon .material-symbols-rounded {
    font-size: 3rem;
    color: white;
}

.modal-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    text-align: center;
}

.modal-message {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    line-height: 1.5;
    margin: 0;
}

.modal-btn {
    width: 100%;
    padding: 0.75rem;
    background: linear-gradient(135deg, #C6923D, #DFC05E);
    color: #000000;
    border: none;
    border-radius: 12px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    margin-top: 0.5rem;
}

.modal-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(198, 146, 61, 0.4);
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
</style>
